import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
// This service straddles the migration from the legacy generated types
// (@dealstack/shared/lib/generated) to the typed Hey API client
// (@dealstack/shared/lib/backendClient). Where a DTO exists under BOTH names,
// take the migrated one from /backendClient; importing the same-named type from
// /generated would still compile but silently diverge if the two specs drift.
// Prefer /backendClient for any new usage, and move the remaining /generated
// imports over as the migration continues. The bulk-delete, bulk-download, and
// add/remove-persons-to-documents endpoints now go directly through the typed
// Hey API client from their hooks — see useBulkDeleteDocuments,
// useBulkDocumentDownload, useAddPersonsToDocuments, useRemovePersonsFromDocuments.
import { BulkUpdateDocumentClassifiersDto } from '@dealstack/shared/lib/backendClient';
import {
	AddPersonsToDocumentDto,
	DocumentsRes,
	GetDocumentRes,
	RemovePersonsFromDocumentDto,
} from '@dealstack/shared/lib/generated';
import {
	DocumentLibraryFilter,
	EditDocumentDto,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

export class DocumentLibraryService {
	static readonly get = async <T>(endpoint: string, filters: unknown, signal?: AbortSignal) => {
		const response = await makeRequest<ApiV1.Response<T>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
				queryString: filters as Record<string, unknown>,
			},
			{
				abortSignal: signal,
			}
		);
		return response;
	};

	static getDocument = async (endpoint: string, filters: unknown, signal?: AbortSignal) => {
		const response = await makeRequest<ApiV1.Response<GetDocumentRes>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
				queryString: filters as Record<string, unknown>,
			},
			{
				abortSignal: signal,
			}
		);
		return response;
	};

	static getWithFilteringAndOrder = async (
		endpoint: string,
		filters: DocumentLibraryFilter,
		signal?: AbortSignal
	) => {
		const fromDate = filters?.fieldFilter?.updatedAt?.from;
		const toDate = filters?.fieldFilter?.updatedAt?.to;
		if (fromDate && filters?.fieldFilter?.updatedAt) {
			filters.fieldFilter.updatedAt.from = browserDateToMidnightUTC(fromDate);
		}
		if (toDate && filters?.fieldFilter?.updatedAt) {
			filters.fieldFilter.updatedAt.to = browserDateToMidnightUTC(toDate);
		}
		return await makeRequest<ApiV1.Response<DocumentsRes>>(
			{
				endpoint,
				method: HTTP_METHOD.GET,
				queryString: filters,
			},
			{ abortSignal: signal }
		);
	};

	static bulkUpdateDocumentClassifiers = async ({
		endpoint,
		filters,
		body,
		signal,
	}: {
		endpoint: string;
		filters: DocumentLibraryFilter;
		body: BulkUpdateDocumentClassifiersDto;
		signal?: AbortSignal;
	}) => {
		const fromDate = filters?.fieldFilter?.updatedAt?.from;
		const toDate = filters?.fieldFilter?.updatedAt?.to;
		if (fromDate && filters?.fieldFilter?.updatedAt) {
			filters.fieldFilter.updatedAt.from = browserDateToMidnightUTC(fromDate);
		}
		if (toDate && filters?.fieldFilter?.updatedAt) {
			filters.fieldFilter.updatedAt.to = browserDateToMidnightUTC(toDate);
		}

		return await makeRequest<ApiV1.Response<DocumentsRes>>(
			{
				endpoint,
				method: HTTP_METHOD.PUT,
				queryString: filters,
				body: JSON.stringify(body),
			},
			{ abortSignal: signal }
		);
	};

	static updateDocument = async ({
		dealId,
		documentId,
		updateData,
	}: {
		updateData: EditDocumentDto;
		documentId: string;
		dealId: string;
	}): Promise<string> => {
		const response = await makeRequest<{ id: string }>({
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_DOCUMENTID_PUT,
				{
					dealId,
					documentId,
				}
			),
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({ updateData }),
		});

		return response.id;
	};

	static removePersonsFromDocument = async ({
		dealId,
		personIds,
		documentId,
	}: {
		personIds: string[];
		dealId: string;
		documentId: string;
	}) => {
		const body: RemovePersonsFromDocumentDto = {
			personsToRemove: personIds,
		};
		const response = await makeRequest({
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_REMOVE_PERSONS_FROM_DOCUMENT_DOCUMENTID_POST,
				{
					dealId,
					documentId,
				}
			),
			method: HTTP_METHOD.POST,
			body: JSON.stringify(body),
		});

		return response;
	};

	static addPersonsToDocument = async ({
		dealId,
		personIds,
		documentId,
	}: {
		personIds: string[];
		dealId: string;
		documentId: string;
	}) => {
		const body: AddPersonsToDocumentDto = {
			personsToAdd: personIds,
		};
		const response = await makeRequest<{ payload: { downloadUrl: string; isAsync: boolean } }>({
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_ADD_PERSONS_TO_DOCUMENT_DOCUMENTID_POST,
				{
					dealId,
					documentId,
				}
			),
			method: HTTP_METHOD.POST,
			body: JSON.stringify(body),
		});

		return response;
	};
}
